import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';
import { getQuestionRubrics } from 'legacy-src/queries';
import { LoadingSpinner, observable } from 'legacy-packages/ft-cb';
import ScoringRubrics from 'legacy-src/components/scoring/ScoringRubrics';
import LearnosityProvider from 'legacy-src/utility/LearnosityProvider';
import { fymClient } from 'legacy-src/utility/GraphqlClients';
import * as types from 'legacy-src/constants/types';
import log from 'chameleon/ui-stack/utilities/log';
import { setMathJaxTypeset } from 'chameleon/src/utility/utils';

/**
 * Draws the rubric guidelines
 * - Prepares the props for the rubric guidelines
 * */
const FrqRubrics = (props) => {
  const {
    question,
    learnosityServerData,
    learnosityQuestion,
    guidelinesHelp,
    shouldAutoScroll,
    playerContainer,
    withRubricsLoading,
  } = props;
  const [rubricIndex, setRubricIndex] = useState(0);
  const [isRubricReady, setRubricReady] = useState(false);
  const [, setIsVisible] = useState(false);
  const [tabsRef, setTasRef] = useState(null);
  const [updated, setUpdated] = useState(null);
  const layoutElement = useRef(null);
  const { reference } = learnosityQuestion;

  const gotToPreviousScoringSection = () => {
    setRubricIndex(rubricIndex - 1);
  };

  const gotToNextScoringSection = () => {
    setRubricIndex(rubricIndex + 1);
  };

  const onTabRubricChange = (index) => {
    setRubricIndex(index);
  };

  const tabsActionCallback = (ref) => {
    ref && !tabsRef && setTasRef(ref);
  };

  useEffect(() => {
    const subscribeIsVisible = (isVisible) => {
      setIsVisible(isVisible);
    };
    observable.subscribe('isVisible', subscribeIsVisible);

    const subscribeUpdated = (updateTime) => {
      setUpdated(updateTime);
    };
    observable.subscribe(`update-current-${reference}`, subscribeUpdated);

    return () => {
      observable.unsubscribe('isVisible', subscribeIsVisible);
      observable.unsubscribe(`update-current-${reference}`, subscribeUpdated);
    };
  }, []);

  useEffect(() => {
    if (tabsRef && tabsRef.updateIndicator && updated) {
      tabsRef.updateIndicator();
    }
  }, [tabsRef, updated]);

  useEffect(() => {
    isRubricReady &&
      layoutElement.current &&
      setMathJaxTypeset(layoutElement.current);
  }, [isRubricReady]);

  if (withRubricsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="ScoringLayout" ref={layoutElement}>
      <div className="block">
        <div className="content">
          <ScoringRubrics
            data-test-rubrics
            question={question}
            hasNextQuestion={false}
            hasPreviousQuestion={false}
            gotToPreviousScoringSection={gotToPreviousScoringSection}
            gotToNextScoringSection={gotToNextScoringSection}
            onTabChange={onTabRubricChange}
            rubricIndex={rubricIndex}
            isLoading={!isRubricReady}
            tabsActionCallback={tabsActionCallback}
            guidelinesHelp={guidelinesHelp}
          />
          {learnosityServerData && (
            <LearnosityProvider
              initOptions={learnosityServerData}
              apiType="items"
              readyListener={() => {
                try {
                  // for all rubric parts, mark the last tab as active
                  const references = Array.from(
                    question.children || [],
                    (child) => child.learnosityItemReference,
                  );

                  references.length &&
                    references.forEach((ref) => {
                      const options = playerContainer.querySelectorAll(
                        `div[data-reference="${ref}"] fieldset .tabs input[type="radio"]`,
                      );
                      if (options.length) {
                        options[options.length - 1].click();
                      }
                    });

                  // for student practice assignments scroll to guidelines automatically
                  if (shouldAutoScroll) {
                    layoutElement.current &&
                      layoutElement.current.scrollIntoView();
                    // account for top header height on iPhone
                    window.scroll(0, window.scrollY - 100);
                  }
                } catch (error) {
                  //Preview rubrics categories/scores not found
                  log('error', error);
                }
                setRubricReady(true);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

FrqRubrics.displayName = 'FrqRubrics';

FrqRubrics.propTypes = {
  /**
   * Question object
   * */
  question: PropTypes.object.isRequired,
  learnosityServerData: PropTypes.object,
  isPreApSubject: PropTypes.bool.isRequired,
  playerContainer: PropTypes.instanceOf(Element),
};

FrqRubrics.defaultProps = {
  learnosityServerData: null,
  playerContainer: document,
};

const withRubrics = graphql(getQuestionRubrics, {
  options: ({ question: { itemId }, isPreApSubject }) => ({
    client: fymClient,
    fetchPolicy: 'network-only',
    variables: {
      questionId: itemId,
      program: isPreApSubject ? types.PreAP : types.AP,
    },
  }),
  props: ({ data: { questionRubricPreview, error, loading } }) => {
    if (error) {
      return {
        error: error.message,
      };
    }

    if (loading) {
      return {
        withRubricsLoading: loading,
      };
    }

    if (questionRubricPreview) {
      return {
        withRubricsLoading: loading,
        learnosityServerData: JSON.parse(
          questionRubricPreview.learnositySignedRequest,
        ),
      };
    }
  },
});

export default compose(withRubrics)(FrqRubrics);
